import React, { forwardRef, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
// the app's translations, also where a page's test renders the field without the app
import 'i18n'
import './searchBox.scss'

// The search field of every CP list (Tudor 2026-10-29, variant A of the canvas „X pentru căutare — CP și SportyaOS”):
// the input and, while it holds text, the „×” that empties the search at once and keeps the focus in the field —
// `onClear` lets the page drop the search from its list and its address without its typing delay; default
// `onValueChange('')`. Escape with text does the same and stops there, so a modal around the field closes on the next
// Escape only. `look="pill"`: the accounts lists' rounded field with the magnifier (Skote `.search-box`);
// `look="plain"`: the form-control the other lists put in their own <label>. Every other input attribute (id,
// maxLength, placeholder, aria-label, autoFocus) passes through; the ref is the input's.
const SearchBox = forwardRef(function SearchBox({
  value, onValueChange, onClear, look = 'plain', className = '', inputClassName = '', clearLabel, disabled, onKeyDown,
  onKeyUp, ...props
}, ref) {
  const { t } = useTranslation()
  const input = useRef(null)
  const emptiedByEscape = useRef(false)
  const setInput = useCallback(node => {
    input.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }, [ref])
  const text = value ?? ''

  const clear = () => {
    if (input.current) input.current.focus()
    if (onClear) onClear()
    else onValueChange('')
  }

  return <span className={`cp-search-box cp-search-box-${look}${look === 'pill' ? ' search-box' : ''} ${className}`.trim()}>
    <span className="cp-search-box-field">
      <input {...props} ref={setInput} type="search" className={`form-control ${inputClassName}`.trim()} value={text}
        disabled={disabled} onChange={event => onValueChange(event.target.value)}
        onKeyDown={event => {
          if (onKeyDown) onKeyDown(event)
          // a caller that stops the Escape keeps the text; an empty field lets Escape through to the modal around it
          if (event.key !== 'Escape' || event.isPropagationStopped() || !text) return
          event.preventDefault()
          event.stopPropagation()
          emptiedByEscape.current = true
          clear()
        }}
        onKeyUp={event => {
          if (onKeyUp) onKeyUp(event)
          // reactstrap's Modal closes on Escape's keyup: the Escape that emptied the field stops there
          if (event.key !== 'Escape' || !emptiedByEscape.current) return
          emptiedByEscape.current = false
          event.stopPropagation()
        }} />
      {look === 'pill' ? <i className="bx bx-search-alt search-icon" aria-hidden="true" /> : null}
      {text && !disabled ? <button type="button" className="cp-search-box-clear" aria-label={clearLabel || t('Clear search')}
        onClick={clear}>
        <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M13 13L1 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button> : null}
    </span>
  </span>
})

export default SearchBox
